import React, { useId } from "react";

/**
 * RadioGroup — grupo de radio buttons controlado
 * @param {string}   value     - valor seleccionado actualmente
 * @param {function} onChange  - recibe el nuevo valor (string)
 * @param {Array}    options   - [{ value, label, disabled? }]
 * @param {string}   name      - name del grupo (se autogenera si no se pasa)
 * @param {"primary"|"secondary"|"accent"|"success"|"warning"|"error"} color
 * @param {"horizontal"|"vertical"} direction
 */
export default function RadioGroup({
    value,
    onChange,
    options = [],
    name,
    color = "primary",
    direction = "horizontal",
    disabled = false,
}) {
    const autoId = useId();
    const groupName = name ?? autoId;

    const colorMap = {
        primary:   { ring: "bg-primary",   border: "border-primary" },
        secondary: { ring: "bg-secondary", border: "border-secondary" },
        accent:    { ring: "bg-accent",    border: "border-accent" },
        success:   { ring: "bg-success",   border: "border-success" },
        warning:   { ring: "bg-warning",   border: "border-warning" },
        error:     { ring: "bg-error",     border: "border-error" },
    };

    const c = colorMap[color] ?? colorMap.primary;

    return (
        <div className={`flex gap-4 ${direction === "vertical" ? "flex-col" : "flex-row flex-wrap"}`}>
            {options.map(opt => {
                const isSelected = value === opt.value;
                const isDisabled = disabled || opt.disabled;

                return (
                    <label
                        key={opt.value}
                        className={`inline-flex items-center gap-2 select-none ${isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                        {/* Círculo visual personalizado */}
                        <span
                            onClick={() => !isDisabled && onChange?.(opt.value)}
                            className={`
                                w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                                transition-colors duration-150
                                ${isSelected ? c.border : "border-gray-400 dark:border-gray-500"}
                            `}
                        >
                            {isSelected && (
                                <span className={`w-2.5 h-2.5 rounded-full ${c.ring}`} />
                            )}
                        </span>

                        {/* Input nativo oculto (accesibilidad) */}
                        <input
                            type="radio"
                            name={groupName}
                            value={opt.value}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => onChange?.(opt.value)}
                            className="sr-only"
                        />

                        <span className="text-sm dark:text-gray-200 text-gray-700 leading-none">
                            {opt.label}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}
